import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Card, Row, Col, Typography, Button, Spin, Empty, Image as AntdImage, Modal, Form, Input, message, Slider, Radio } from "antd";
import { FolderFilled, PlusOutlined, ArrowLeftOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { createImageFolder, getImageFolders, uploadImages, updateImageFolder, deleteImage, } from "@/redux/actions/imageActions";
import ImageSelector from "@/components/ImageSelector";
import Cropper from "react-easy-crop";
import { checkPermission } from "@/utils/permission";
import { isMobile } from "react-device-detect";

const { Text } = Typography;
const { confirm } = Modal;

const ImageTab = ({ customer }) => {
    const dispatch = useDispatch();
    const customerId = customer?.id || null;

    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [images, setImages] = useState([]);
    const [resetKey, setResetKey] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingFolder, setEditingFolder] = useState(null); // folder đang sửa
    const [form] = Form.useForm();

    const [cropModalVisible, setCropModalVisible] = useState(false);
    const [croppingImage, setCroppingImage] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [rotation, setRotation] = useState(0);
    const [croppedArea, setCroppedArea] = useState(null);
    const [guideVisible, setGuideVisible] = useState(false);

    const allowUpdateFolder = checkPermission('images', "update_folder");
    const allowDeleteImage = checkPermission('images', "delete_image");

    const CROP_ASPECT = 4 / 3; // đồng bộ với prop aspect của <Cropper />

    const [aspect, setAspect] = useState(CROP_ASPECT); // null => free

    const [cropperKey, setCropperKey] = useState(0);

    /** Hàm tải lại danh sách thư mục */
    const loadFolders = async () => {
        if (!customerId) return;
        setLoading(true);
        try {
            const res = await dispatch(getImageFolders(customerId));
            const data = res.data || [];
            setFolders(data);

            // nếu đang mở folder thì sync lại
            if (selectedFolder) {
                const updatedFolder = data.find((f) => f.id === selectedFolder.id);
                if (updatedFolder) {
                    setImages(updatedFolder.images || []);
                    setSelectedFolder(updatedFolder);
                }
            }
        } catch {
            setFolders([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadFolders();
    }, [dispatch, customerId]);

    const handleOpenFolder = (folder) => {
        setSelectedFolder(folder);
        setImages(folder.images || []);
    };

    /** Upload ảnh */
    const handleImageChange = async (fileList, changeDetails) => {
        if (changeDetails.action === "reset") return;
        const images = fileList.map((file) => ({
            manual_id: file.manual_id,
            src: file.originalImage,
        }));

        console.log('images', images);

        try {
            if (changeDetails.action === "add") {
                setUploading(true);
                await dispatch(uploadImages(selectedFolder.id, { images }));
                await loadFolders();
                setResetKey((prev) => prev + 1);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setUploading(false); // tắt loading
        }
    };

    /** Mở modal tạo thư mục mới */
    const openCreateFolderModal = () => {
        setEditingFolder(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    /** Mở modal sửa thư mục */
    const openEditFolderModal = (folder) => {
        setEditingFolder(folder);
        form.setFieldsValue({
            name: folder.name,
            description: folder.description,
        });
        setIsModalVisible(true);
    };

    /** Lưu thư mục (tạo hoặc sửa) */
    const handleSaveFolder = async () => {
        try {
            const values = await form.validateFields();
            if (editingFolder) {
                // Sửa
                await dispatch(
                    updateImageFolder(editingFolder.id, {
                        name: values.name,
                        description: values.description,
                    })
                );
            } else {
                // Tạo mới
                await dispatch(
                    createImageFolder({
                        customer_id: customerId,
                        name: values.name,
                        description: values.description,
                    })
                );
            }
            setIsModalVisible(false);
            await loadFolders();
        } catch (err) {
            console.error(err);
        }
    };

    // Hàm xử lý xoá ảnh
    const handleDeleteImage = (img) => {
        confirm({
            title: "Xác nhận",
            content: "Bạn có chắc chắn muốn xoá ảnh này?",
            okText: "Xoá",
            cancelText: "Huỷ",
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await dispatch(deleteImage(img.id));
                    await loadFolders();
                } catch (err) {
                    console.error(err);
                }
            },
        });
    };

    const showCropModal = (img) => {
        setCroppingImage(img);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
        setCroppedArea(null);
        setCropModalVisible(true);
        setAspect(CROP_ASPECT);
        // mỗi lần mở modal thì remount Cropper
        setCropperKey(prev => prev + 1);
    };

    const onCropComplete = (_, croppedAreaPx) => {
        setCroppedAreaPixels(croppedAreaPx);
    };


    const getCroppedImg = async (imageSrc, cropPixels, rotation = 0, zoom = 1) => {
        // Clean URL (tránh redirect gây drop CORS)
        const cleanSrc = imageSrc.split("?")[0];

        const image = new Image();
        image.crossOrigin = "anonymous";
        image.referrerPolicy = "no-referrer";

        // Gán src SAU khi set crossOrigin
        image.src = cleanSrc + "?v=" + Date.now();  // phá cache tuyệt đối

        try {
            await image.decode();
        } catch (err) {
            console.error("Decode error:", err);
            throw err;
        }

        // Fallback cropPixels TRƯỚC
        if (!cropPixels || !cropPixels.width || !cropPixels.height) {
            cropPixels = {
                x: 0,
                y: 0,
                width: image.width,
                height: image.height
            };
        }

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const radians = (rotation * Math.PI) / 180;

        // Kích thước thực sau zoom
        const width = image.width * zoom;
        const height = image.height * zoom;

        // Canvas phải theo kích thước vùng crop
        canvas.width = cropPixels.width;
        canvas.height = cropPixels.height;

        // Dịch chuyển – xoay
        ctx.translate(-cropPixels.x, -cropPixels.y);
        ctx.translate(width / 2, height / 2);
        ctx.rotate(radians);

        // Vẽ ảnh
        ctx.drawImage(
            image,
            -image.width / 2,
            -image.height / 2,
            image.width,
            image.height
        );

        return canvas.toDataURL("image/jpeg", 0.92);
    };

    const handleSaveCropped = async () => {
        try {
            const croppedBase64 = await getCroppedImg(croppingImage.file_url_original, croppedAreaPixels, rotation);
            //console.log('croppedBase64', croppedBase64);

            setSaveLoading(true);

            // Upload ảnh mới
            await dispatch(uploadImages(selectedFolder.id, {
                images: [{ manual_id: Date.now(), src: croppedBase64 }]
            }));

            // Xóa ảnh gốc sau khi lưu thành công
            if (croppingImage?.id) {
                await dispatch(deleteImage(croppingImage.id));
            }

            // Reload lại thư mục để hiển thị ảnh mới
            await loadFolders();

        } catch (err) {
            console.error(err);
            message.error("Lưu ảnh thất bại");
        } finally {
            setSaveLoading(false);
            setCropModalVisible(false);
            setRotation(0);
            setZoom(1);
            setAspect(CROP_ASPECT);
        }
    };

    const OutputViewer = ({ image, croppedArea, rotation = 0, aspect = CROP_ASPECT }) => {
        if (!croppedArea) return null;

        const scale = 100 / croppedArea.width;

        const transform = {
            x: `${-croppedArea.x * scale}%`,
            y: `${-croppedArea.y * scale}%`,
            scale,
            width: "calc(100% + 0.5px)",
            height: "auto"
        };

        const imageStyle = {
            transform: `
                translate3d(${transform.x}, ${transform.y}, 0) 
                scale3d(${transform.scale},${transform.scale},1) 
                rotate(${rotation}deg)`,
            width: transform.width,
            height: transform.height,
            //transformOrigin: "center center",
        };

        return (
            <div
                className="output-viewer"
                style={{
                    paddingBottom: `${100 / aspect}%`,
                    overflow: "hidden",
                    position: "relative",
                }}
            >
                <img src={image} alt="preview" style={imageStyle} />
            </div>
        );
    };

    useEffect(() => {
        setCrop({ x: 0, y: 0 });
    }, [aspect]);

    return (
        <div>
            {/* Thanh công cụ */}
            <div style={{ marginBottom: 30 }} className="mt-4">
                <Row gutter={8} justify="space-between" align="middle">
                    <Col>
                        {selectedFolder ? (
                            <Button
                                icon={<ArrowLeftOutlined />}
                                onClick={() => setSelectedFolder(null)}
                            >
                                Quay lại thư mục
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openCreateFolderModal}
                            >
                                Tạo thư mục mới
                            </Button>
                        )}
                    </Col>
                    <Col>
                        <Button
                            type="link"
                            icon={<QuestionCircleOutlined />}
                            style={{
                                color: "#1677ff",
                                fontWeight: 500,
                                fontSize: 15,
                            }}
                            onClick={() => setGuideVisible(true)}
                        >
                            Xem hướng dẫn chụp ảnh
                        </Button>
                    </Col>
                </Row>
            </div>

            {/* Danh sách thư mục */}
            {!selectedFolder ? (
                loading ? (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: "100px",
                            width: "100%",
                        }}
                    >
                        <Spin />
                    </div>
                ) : folders.length > 0 ? (
                    <Row gutter={[24, 24]}>
                        {folders.map((folder) => (
                            <Col key={folder.id}>
                                <Card
                                    hoverable
                                    onClick={() => handleOpenFolder(folder)}
                                    style={{
                                        width: 120,
                                        textAlign: "center",
                                        border: "none",
                                        boxShadow: "none",
                                    }}
                                    bodyStyle={{ padding: 0 }}
                                >
                                    <FolderFilled style={{ fontSize: 80, color: "#f4a300" }} />
                                    <div>
                                        <Text style={{ fontSize: 12 }}>
                                            {folder.name}
                                            {folder.images && folder.images.length > 0 && ` (${folder.images.length})`}
                                        </Text>
                                    </div>
                                    <Button
                                        size="small"
                                        style={{ marginTop: 8 }}
                                        disabled={!allowUpdateFolder || folder.type === "default"}
                                        onClick={(e) => {
                                            e.stopPropagation(); // tránh mở folder
                                            openEditFolderModal(folder);
                                        }}
                                    >
                                        Sửa
                                    </Button>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                ) : (
                    <Empty description="Chưa có thư mục ảnh" />
                )
            ) : (
                // Xem chi tiết folder
                <div>
                    <Text strong style={{ display: "block", marginBottom: 20 }}>
                        Thư mục: {selectedFolder.name}
                    </Text>

                    <ImageSelector
                        onChangeData={handleImageChange}
                        multiple
                        maxCount={50}
                        uploadText="Chọn ảnh để tải lên"
                        textSize={15}
                        width={100}
                        height={100}
                        resetSignal={resetKey}
                    />

                    <div style={{ marginTop: 20 }}>
                        <Spin spinning={uploading} tip="Đang upload ảnh...">
                            {images.length > 0 ? (
                                <AntdImage.PreviewGroup>
                                    <Row gutter={[16, 16]} style={{ width: "100%", margin: 0 }}>
                                        {images.map((img) => (
                                            <Col
                                                key={img.id}
                                                xs={24} sm={12} md={8} lg={6} xl={6}   // responsive: full trên mobile, nhiều cột hơn khi rộng
                                                style={{ display: "flex", justifyContent: "center" }}
                                            >
                                                <div
                                                    style={{
                                                        position: "relative",
                                                        width: "100%",             // chiếm hết cột
                                                        aspectRatio: "4 / 3",      // tỷ lệ 4:3
                                                        border: "1px solid #f0f0f0",
                                                        borderRadius: 4,
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            background: "#f9f9f9d0", // màu nền khi ảnh nhỏ hơn khung
                                                        }}
                                                    >
                                                        <AntdImage
                                                            src={img.file_url_thumb}
                                                            preview={{
                                                                src: img.file_url_original + "?v=" + Date.now()
                                                            }}
                                                            alt={img.file_name}
                                                            style={{
                                                                width: "100%",   // không vượt quá khung
                                                                height: "100%",  // luôn giữ tỷ lệ
                                                                objectFit: "cover", // luôn hiển thị toàn bộ ảnh
                                                                objectPosition: "center center",
                                                            }}
                                                        />
                                                    </div>

                                                    <Button
                                                        size="small"
                                                        style={{ position: "absolute", top: 8, left: 8, zIndex: 2 }}
                                                        type="primary"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            showCropModal(img);
                                                        }}
                                                    >
                                                        Cắt ảnh
                                                    </Button>
                                                    <Button
                                                        danger
                                                        size="small"
                                                        style={{
                                                            position: "absolute",
                                                            top: 8,
                                                            right: 8,
                                                            zIndex: 2,
                                                        }}
                                                        disabled={!allowDeleteImage}
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // tránh mở preview
                                                            handleDeleteImage(img);
                                                        }}
                                                    >
                                                        Xoá
                                                    </Button>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </AntdImage.PreviewGroup>
                            ) : (
                                <Empty description="Chưa có ảnh trong thư mục này" />
                            )}
                        </Spin>
                    </div>
                </div>
            )}

            {/* Modal tạo/sửa thư mục */}
            <Modal
                open={isModalVisible}
                title={editingFolder ? "Sửa thư mục" : "Tạo thư mục mới"}
                onCancel={() => setIsModalVisible(false)}
                onOk={handleSaveFolder}
                okText={editingFolder ? "Lưu" : "Tạo"}
                cancelText="Hủy"
                maskClosable={false}
            >
                <Form layout="vertical" form={form}>
                    <Form.Item
                        label="Tên thư mục"
                        name="name"
                        rules={[{ required: true, message: "Vui lòng nhập tên thư mục" }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item label="Mô tả" name="description">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                open={cropModalVisible}
                title="Cắt ảnh"
                onOk={handleSaveCropped}
                onCancel={() => {
                    setCropModalVisible(false);
                    setAspect(CROP_ASPECT);
                }}
                okText="Lưu ảnh mới"
                cancelText="Huỷ"
                width={800}
                maskClosable={false}
                confirmLoading={saveLoading}
                style={{ top: 10 }}
            >
                <Row gutter={16}>
                    <Col span={24}>
                        <div style={{ width: "100%", height: 400, position: "relative" }}>
                            <Cropper
                                key={cropperKey}  // remount khi thay đổi key
                                image={croppingImage?.file_url_original}
                                crop={crop}
                                zoom={zoom}
                                aspect={aspect} // dynamic
                                rotation={rotation}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}      // để lưu ảnh
                                onCropAreaChange={setCroppedArea}    // để preview realtime
                                onRotationChange={setRotation}
                            />
                        </div>
                        <div style={{ marginTop: 20 }}>
                            <div style={{ marginBottom: 15 }}>
                                <Text>Chọn tỷ lệ khung cắt:</Text>
                                <Radio.Group
                                    value={aspect}
                                    onChange={(e) => setAspect(e.target.value)}
                                    style={{ marginLeft: 12 }}
                                >
                                    <Radio.Button value={1}>1:1</Radio.Button>
                                    <Radio.Button value={4 / 3}>4:3</Radio.Button>
                                    <Radio.Button value={3 / 4}>3:4</Radio.Button>
                                    <Radio.Button value={16 / 9}>16:9</Radio.Button>
                                    <Radio.Button value={9 / 16}>9:16</Radio.Button>
                                    <Radio.Button value={2 / 3}>2:3</Radio.Button>
                                    <Radio.Button value={5 / 4}>5:4</Radio.Button>
                                </Radio.Group>
                            </div>
                            <div style={{ marginBottom: 10 }}>
                                <Text>Zoom:</Text>
                                <Slider
                                    min={1}
                                    max={3}
                                    step={0.1}
                                    value={zoom}
                                    onChange={setZoom}
                                />
                            </div>
                            <div>
                                <Text>Xoay:</Text>
                                <Slider
                                    min={0}
                                    max={360}
                                    step={1}
                                    value={rotation}
                                    onChange={setRotation}
                                />
                            </div>
                            <Button onClick={() => { setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0); }} style={{ marginTop: 10 }}>
                                Reset
                            </Button>
                        </div>
                    </Col>
                    {/** Preview không chỉnh xác khi sử dụng rotation, tạm thời không dùng */}
                    {/* <Col span={12}>
                        <div className="viewer">
                            
                            {croppedArea ? (
                                <OutputViewer
                                    image={croppingImage?.file_url}
                                    croppedArea={croppedArea}
                                    aspect={CROP_ASPECT}
                                    rotation={rotation}
                                />
                            ) : (
                                <Text type="secondary">Kéo cắt ảnh để xem trước…</Text>
                            )}
                        </div>
                    </Col> */}
                </Row>
            </Modal>

            <Modal
                open={guideVisible}
                title="Hướng dẫn chụp ảnh đúng cách"
                onCancel={() => setGuideVisible(false)}
                footer={null}
                width={isMobile ? "100%" : 900}
            >
                <p>Dưới đây là hướng dẫn cách chụp ảnh hồ sơ bệnh nhân đúng chuẩn:</p>
                <AntdImage.PreviewGroup>
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <AntdImage
                                src="/guide-images/goc-chup-trong.jpg"
                                style={{ width: "100%", borderRadius: 8 }}
                                preview={false}
                            />
                        </Col>
                        <Col span={24}>
                            <AntdImage
                                src="/guide-images/goc-chup-ngoai.jpg"
                                style={{ width: "100%", borderRadius: 8 }}
                                preview={false}
                            />
                        </Col>
                    </Row>
                </AntdImage.PreviewGroup>
            </Modal>
        </div>
    );
};

export default ImageTab;
